<script lang="ts">
  export interface PhoneInputProps {
    modelValue?: string | null
    countryCode?: string
    placeholder?: string
    disabled?: boolean
    id?: string
    dataTestid?: string
    autofocus?: boolean
    ariaInvalid?: boolean
    class?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { ref, watch, nextTick, computed, inject } from 'vue'
  import type { HTMLAttributes } from 'vue'
  import { parsePhoneNumber } from 'awesome-phonenumber'
  import { cn } from '../../../lib/utils'
  import { Input } from '../input'
  import { FIELD_CONTEXT_KEY } from '../field'
  import { phoneCodeMapping } from './all-countries'

  interface Country {
    name: string
    code: string
    dialCode: string
  }

  const countries: Country[] = phoneCodeMapping.map((entry) => ({
    name: String(entry[0]),
    code: String(entry[1]).toUpperCase(),
    dialCode: String(entry[2])
  }))

  /**
   * A pure, borderless phone text input. The user types the full number
   * (e.g. "+4790653275"); it auto-detects the country and, on blur, formats
   * to international style ("+47 906 53 27"). It carries NO country selector
   * UI of its own — the detected region is surfaced via `update:countryCode`
   * so a parent (e.g. `InputGroupPhone`) can render the flag / dropdown.
   *
   * It brings no border — it tags its input `data-slot="input-group-control"`
   * so a surrounding `InputGroup` owns the focus / error chrome (exactly like
   * `InputGroupInput`). Pass `country-code` in to prepend / swap the dial code
   * from an external country selector, or call the exposed `selectCountry()`.
   *
   * Model value: E.164 string or undefined.
   */
  const props = defineProps<PhoneInputProps>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string | undefined): void
    (e: 'update:countryCode', value: string | undefined): void
    (e: 'blur', event: FocusEvent): void
  }>()

  // ── Internal state ──────────────────────────────────────────────────

  const inputText = ref('')
  const detectedCountry = ref<Country | undefined>()
  const inputRef = ref<InstanceType<typeof Input> | null>(null)

  // ── Country helpers ─────────────────────────────────────────────────

  function findCountryByCode(code: string | undefined): Country | undefined {
    if (!code) return undefined
    return countries.find((c) => c.code.toUpperCase() === code.toUpperCase())
  }

  function detectCountryFromText(text: string): Country | undefined {
    if (!text) return undefined
    const parsed = parsePhoneNumber(text)
    if (parsed.valid && parsed.regionCode) {
      return findCountryByCode(parsed.regionCode)
    }
    // Partial match: try to find a dial code prefix
    if (text.startsWith('+')) {
      const withoutPlus = text.slice(1).replace(/\s/g, '')
      const sorted = [...countries].sort(
        (a, b) => b.dialCode.length - a.dialCode.length
      )
      for (const c of sorted) {
        if (withoutPlus.startsWith(c.dialCode)) return c
      }
      // Still typing — try partial dial code
      for (const c of sorted) {
        if (c.dialCode.startsWith(withoutPlus) && withoutPlus.length > 0) {
          return c
        }
      }
    }
    return undefined
  }

  // ── Format helpers ──────────────────────────────────────────────────

  function formatForDisplay(e164: string | null | undefined): string {
    if (!e164 || e164.trim() === '') return ''
    const parsed = parsePhoneNumber(e164)
    if (parsed.valid && parsed.number?.international) {
      return parsed.number.international
    }
    return e164
  }

  function toE164(text: string): string | undefined {
    if (!text || text.trim() === '') return undefined

    // "Just a dial code" (no national number) is a UI prefill from the
    // country picker, not a user-entered value — treat it as empty so
    // validators, submit payloads, and "is the field empty?" checks all
    // agree. Detect by comparing the digits-only form to the known set.
    const digitsOnly = text.replace(/\D/g, '')
    if (digitsOnly && countries.some((c) => c.dialCode === digitsOnly)) {
      return undefined
    }

    const parsed = parsePhoneNumber(text)
    if (parsed.valid && parsed.number?.e164) {
      return parsed.number.e164
    }
    // Return raw text with non-digits stripped if it starts with +
    const cleaned = text.replace(/[^\d+]/g, '')
    return cleaned || undefined
  }

  function extractNational(text: string): string {
    if (!text) return ''
    const parsed = parsePhoneNumber(text)
    if (parsed.valid && parsed.number?.national) {
      return parsed.number.national
    }
    if (text.startsWith('+')) {
      const match = text.match(/^\+\d+\s*(.*)$/)
      return match?.[1] ?? ''
    }
    return text
  }

  // ── Sync from props ─────────────────────────────────────────────────

  let isInternalUpdate = false

  watch(
    () => props.modelValue,
    (val) => {
      if (isInternalUpdate) return
      inputText.value = formatForDisplay(val)
      detectedCountry.value = detectCountryFromText(val ?? '')
      emit('update:countryCode', detectedCountry.value?.code)
    },
    { immediate: true }
  )

  watch(
    () => props.countryCode,
    (code) => {
      if (!code) return
      const country = findCountryByCode(code)
      if (country && country.code !== detectedCountry.value?.code) {
        detectedCountry.value = country
        // Prepend dial code if input is empty or has a different code
        if (!inputText.value || !inputText.value.startsWith('+')) {
          inputText.value = `+${country.dialCode} ${inputText.value}`.trim()
          emitValue()
        }
      }
    }
  )

  // ── Input handlers ──────────────────────────────────────────────────

  function onInput(value: string | number) {
    inputText.value = String(value)
    detectedCountry.value = detectCountryFromText(inputText.value)
    emitValue()
  }

  function onBlur(event: FocusEvent) {
    // Format on blur if we have a valid number
    const e164 = toE164(inputText.value)
    if (e164) {
      const formatted = formatForDisplay(e164)
      if (formatted !== inputText.value) {
        inputText.value = formatted
      }
    }
    emit('blur', event)
  }

  function emitValue() {
    isInternalUpdate = true
    const e164 = toE164(inputText.value)
    emit('update:modelValue', e164)
    emit('update:countryCode', detectedCountry.value?.code)
    nextTick(() => {
      isInternalUpdate = false
    })
  }

  /**
   * Set the country explicitly (e.g. from an external country dropdown),
   * swapping the dial code while preserving the typed national number.
   */
  function selectCountry(code: string) {
    const country = findCountryByCode(code)
    if (!country) return
    detectedCountry.value = country
    const currentNational = extractNational(inputText.value)
    inputText.value = `+${country.dialCode} ${currentNational}`.trim()
    emitValue()
  }

  defineExpose({
    focus: () => inputRef.value?.focus(),
    selectCountry
  })

  // The whole app runs under a stacked `FieldRoot`, where a `FieldGroup`
  // strips the height / padding of sibling inputs via reset classes keyed on
  // `data-slot=input`. Our input carries `data-slot=input-group-control` (so
  // the parent InputGroup focus / error ring picks it up), which escapes
  // those resets — so we mirror the stacked height treatment here to stay
  // flush with neighbouring text fields (same as `InputGroupCombobox`).
  const fieldContext = inject(FIELD_CONTEXT_KEY, {
    variant: 'default' as const
  })
  const stacked = computed(() => fieldContext.variant === 'stacked')
</script>

<template>
  <Input
    :id="id"
    ref="inputRef"
    data-slot="input-group-control"
    :model-value="inputText"
    type="tel"
    :aria-invalid="ariaInvalid || undefined"
    :placeholder="placeholder"
    :disabled="disabled"
    :autofocus="autofocus"
    :data-testid="dataTestid"
    :class="
      cn(
        'min-w-0 flex-1 rounded-none border-0 bg-transparent pl-1 pr-3 shadow-none focus-visible:ring-0 dark:bg-transparent',
        stacked ? 'h-auto py-0' : 'h-9',
        props.class
      )
    "
    @update:model-value="onInput"
    @blur="onBlur"
  />
</template>
